import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, AUTH } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";

export default function SubscribeClaim() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!token) {
    return (
      <div>
        <AuthShell />
        <div className="max-w-md mx-auto px-6 py-32 text-center">
          <h1 className="text-3xl mb-4">Missing subscription token</h1>
          <p className="text-muted-foreground"><a href="/subscribe" className="text-primary font-bold">Try subscribing again</a></p>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // First sign in
      const loginR = await api.post("/auth/login", { email, password });
      AUTH.set(loginR.data.token, loginR.data.user);
      // Then claim the subscription
      const claimR = await api.post("/claim-subscription", {
        subscribe_token: token,
        name: loginR.data.user.name,
        email,
        password,
      });
      AUTH.set(claimR.data.token, claimR.data.user);
      toast.success("Subscription linked to your account!");
      nav(`/dashboard/${claimR.data.tenant.slug}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong";
      if (detail.includes("already claimed")) {
        toast.error("This subscription has already been linked to an account.");
        nav("/account");
      } else if (detail.includes("not yet paid")) {
        toast.error("Payment hasn't completed yet — please wait a moment and refresh.");
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <AuthShell />
      <div className="max-w-md mx-auto px-6 py-16">
        <h1 className="text-3xl mb-2">Link your subscription</h1>
        <p className="text-muted-foreground mb-8">Sign in to link this subscription to your existing account.</p>
        <Card className="p-8">
          <form onSubmit={submit} className="space-y-4">
            <Input placeholder="you@business.com" type="email" value={email}
              onChange={e=>setEmail(e.target.value)} required data-testid="claim-email"/>
            <Input placeholder="Password" type="password" value={password}
              onChange={e=>setPassword(e.target.value)} required data-testid="claim-password"/>
            <Button type="submit" disabled={busy} className="w-full h-11 font-bold" data-testid="claim-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Sign in &amp; link <ArrowRight className="w-4 h-4 ml-1"/></>}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground text-center mt-6">
            No account? <Link to={`/subscribe/success?token=${token}`} className="text-primary font-bold">Create one</Link>
          </p>
        </Card>
      </div>
      <SiteFooter />
    </div>
  );
}
